import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import Modal from '../../components/Modal';
import { getUsers, banUser, unbanUser, forceLogoutUser, grantAdmin, revokeAdmin, resetProfile } from '../../api/AdminAPI';
import { getMyUserId } from '../../utils/jwt';

const RESET_PARTS = ['avatar', 'banner', 'text'];

const PAGE_SIZE = 25;

export default function AdminUsers() {
  const { t } = useLanguage();
  const history = useHistory();
  const myUserId = getMyUserId();

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [bannedFilter, setBannedFilter] = useState('');
  const [page, setPage] = useState(1);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [iShow, setiShow] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [banReason, setBanReason] = useState('');
  const [actionError, setActionError] = useState('');

  const token = localStorage.getItem('accessToken');

  const load = async () => {
    setLoading(true);
    const data = await getUsers(token, { search, page, pageSize: PAGE_SIZE, role: roleFilter, banned: bannedFilter });
    if (data.status === 'forbidden') { history.replace('/home'); return; }
    if (data.status === 'success') {
      setItems(data.data.items);
      setTotal(data.data.total);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, roleFilter, bannedFilter]);

  useEffect(() => {
    setPage(1);
    const timeout = setTimeout(load, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Which parts "reset profile" clears - all ticked by default.
  const [resetParts, setResetParts] = useState(RESET_PARTS);

  const openConfirm = (kind, user) => {
    setActionError('');
    setBanReason('');
    setResetParts(RESET_PARTS);
    setModalContent({ kind, user });
    setiShow(true);
  };

  const runAction = async () => {
    if (!modalContent) return;
    const { kind, user } = modalContent;
    let result;
    if (kind === 'ban') result = await banUser(token, user.id, banReason);
    else if (kind === 'unban') result = await unbanUser(token, user.id);
    else if (kind === 'forceLogout') result = await forceLogoutUser(token, user.id);
    else if (kind === 'grantAdmin') result = await grantAdmin(token, user.id);
    else if (kind === 'revokeAdmin') result = await revokeAdmin(token, user.id);
    else if (kind === 'resetProfile') result = await resetProfile(token, user.id, resetParts);

    if (result && result.status === 'success') {
      setiShow(false);
      setModalContent(null);
      load();
    } else {
      setActionError((result && result.message) || t('errors.generic'));
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const formatDate = (d) => (d ? new Date(d).toLocaleString() : t('admin.users.never'));

  // Same relative-time shape as Notifications.js's timeAgo - reused here
  // (not extracted to a shared util) since this is its only other caller.
  const timeAgo = (iso) => {
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return t('notifications.justNow');
    if (mins < 60) return t('notifications.minutesAgo', { count: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t('notifications.hoursAgo', { count: hours });
    const days = Math.floor(hours / 24);
    return t('notifications.daysAgo', { count: days });
  };
  // Absolute timestamp stays the primary read (exact audit value an admin
  // may need), relative time is the at-a-glance addition - "3 sa önce" is
  // far faster to scan down a list of users than comparing raw dates.
  const formatLastActive = (d) => (d ? `${new Date(d).toLocaleString()} (${timeAgo(d)})` : t('admin.users.never'));

  return (
    <div className="PageContainer">
      <Modal mState={{ iShow, setiShow }} buttonShow="yes">
        {modalContent && (
          <>
            <p>{t(`admin.users.confirm${modalContent.kind[0].toUpperCase()}${modalContent.kind.slice(1)}`, { username: modalContent.user.username })}</p>
            {modalContent.kind === 'ban' && (
              <div className="setfield" style={{ marginBottom: 12 }}>
                {t('admin.users.banReasonLabel')}
                <input
                  type="text"
                  value={banReason}
                  placeholder={t('admin.users.banReasonPlaceholder')}
                  autoComplete="off"
                  onChange={(e) => setBanReason(e.target.value)}
                />
              </div>
            )}
            {modalContent.kind === 'resetProfile' && (
              <div className="AdminResetParts">
                {RESET_PARTS.map((part) => (
                  <label key={part}>
                    <input
                      type="checkbox"
                      checked={resetParts.includes(part)}
                      onChange={(e) => setResetParts((prev) => (e.target.checked ? [...prev, part] : prev.filter((p) => p !== part)))}
                    />
                    {t(`admin.users.resetParts.${part}`)}
                  </label>
                ))}
              </div>
            )}
            {actionError && <p style={{ color: 'var(--danger-text)' }}>{actionError}</p>}
            <div className="satirfield" style={{ float: 'left' }}>
              <button onClick={runAction} className="SendButton cancelColor">{t('common.ok')}</button>
              <button onClick={() => setiShow(false)} className="SendButton secondaryColor">{t('common.cancel')}</button>
            </div>
          </>
        )}
      </Modal>

      <div className="PageHeader glass">
        <h4>{t('admin.users.title')}</h4>
        <aside className="descGray">{t('admin.users.subtitle')}</aside>
      </div>
      <div className="Page glass" style={{ overflowY: 'auto' }}>
        <div className="AdminSection">
          <div className="AdminToolbar">
            <input
              type="text"
              placeholder={t('admin.users.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="">{t('admin.users.filterRoleAll')}</option>
              <option value="admin">{t('admin.users.filterRoleAdmin')}</option>
              <option value="user">{t('admin.users.filterRoleUser')}</option>
            </select>
            <select value={bannedFilter} onChange={(e) => setBannedFilter(e.target.value)}>
              <option value="">{t('admin.users.filterBannedAll')}</option>
              <option value="true">{t('admin.users.filterBannedYes')}</option>
              <option value="false">{t('admin.users.filterBannedNo')}</option>
            </select>
          </div>

          {loading ? (
            <p>{t('common.loading')}</p>
          ) : items.length === 0 ? (
            <p className="descGray">{t('admin.users.empty')}</p>
          ) : (
            <div className="AdminTableWrap">
              {/* AdminTableCards: rows become cards on phones (Admin.css) -
                  data-label is what names each field there. */}
              <table className="AdminTable AdminTableCards">
                <thead>
                  <tr>
                    <th>{t('admin.users.columnUser')}</th>
                    <th>{t('admin.users.columnRole')}</th>
                    <th>{t('admin.users.columnStatus')}</th>
                    <th>{t('admin.users.columnCreated')}</th>
                    <th>{t('admin.users.columnLastActive')}</th>
                    <th>{t('admin.users.columnActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <div>{u.username}</div>
                        <div className="descGray" style={{ fontSize: 12 }}>{u.mail}</div>
                      </td>
                      <td data-label={t('admin.users.columnRole')}>{u.role === 'admin' && <span className="AdminBadge AdminBadgeAdmin">{t('admin.users.filterRoleAdmin')}</span>}</td>
                      <td data-label={t('admin.users.columnStatus')}>
                        <span className={u.online ? "AdminBadge AdminBadgeOnline" : "AdminBadge AdminBadgeOffline"}>
                          {u.online ? t('common.online') : t('common.offline')}
                        </span>
                        {u.banned && <span className="AdminBadge AdminBadgeBanned" style={{ marginLeft: 6 }}>{t('admin.users.banned')}</span>}
                      </td>
                      <td data-label={t('admin.users.columnCreated')}>{formatDate(u.createdAt)}</td>
                      <td data-label={t('admin.users.columnLastActive')}>{formatLastActive(u.lastActiveAt)}</td>
                      <td className="AdminTableActionsCell">
                        <div className="AdminActions">
                          {u.banned ? (
                            <button className="secondaryColor" onClick={() => openConfirm('unban', u)}>{t('admin.users.unban')}</button>
                          ) : (
                            u.role !== 'admin' && <button className="cancelColor" onClick={() => openConfirm('ban', u)}>{t('admin.users.ban')}</button>
                          )}
                          <button className="secondaryColor" onClick={() => openConfirm('forceLogout', u)}>{t('admin.users.forceLogout')}</button>
                          <button className="secondaryColor" onClick={() => openConfirm('resetProfile', u)}>{t('admin.users.resetProfile')}</button>
                          {u.role === 'admin' ? (
                            u.id !== myUserId && <button className="secondaryColor" onClick={() => openConfirm('revokeAdmin', u)}>{t('admin.users.revokeAdmin')}</button>
                          ) : (
                            <button className="primaryColor" onClick={() => openConfirm('grantAdmin', u)}>{t('admin.users.grantAdmin')}</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="AdminPagination">
            <button className="SendButton secondaryColor" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>{t('admin.users.prev')}</button>
            <span className="descGray">{t('admin.users.pageInfo', { page, total: totalPages })}</span>
            <button className="SendButton secondaryColor" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>{t('admin.users.next')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
